import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { useModuleDependents } from '../../hooks/subscriptions/useModuleDependencies';

interface ModuleDependentsCellProps {
  moduleKey: string;
}

/**
 * Component to display module dependents in a table cell
 * Shows which modules depend on this module (reverse dependencies)
 */
export const ModuleDependentsCell = ({ moduleKey }: ModuleDependentsCellProps) => {
  const { data: dependents, isLoading } = useModuleDependents(moduleKey, true);

  if (isLoading) {
    return (
      <Chip
        label="Loading..."
        size="small"
        variant="outlined"
        sx={{ fontSize: '0.75rem' }}
      />
    );
  }

  if (!dependents || dependents.length === 0) {
    return (
      <Chip
        label="None"
        size="small"
        variant="outlined"
        sx={{ fontSize: '0.75rem' }}
      />
    );
  }

  // If only 1-2 dependents, show them inline
  if (dependents.length <= 2) {
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {dependents.map((dep) => (
          <Tooltip key={dep.id} title={dep.description} arrow>
            <Chip
              label={dep.name}
              size="small"
              color="warning"
              sx={{
                fontSize: '0.75rem',
                height: '22px',
                backgroundColor: '#ed6c0220',
                color: '#ed6c02',
                fontWeight: 500,
              }}
            />
          </Tooltip>
        ))}
      </Stack>
    );
  }

  // If 3+ dependents, show count with tooltip
  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="caption" fontWeight={600} display="block" mb={0.5}>
            Required by:
          </Typography>
          {dependents.map((dep) => (
            <Typography key={dep.id} variant="caption" display="block">
              • {dep.name}
            </Typography>
          ))}
        </Box>
      }
      arrow
    >
      <Chip
        label={`${dependents.length} modules`}
        size="small"
        color="warning"
        variant="outlined"
        sx={{ fontSize: '0.75rem' }}
      />
    </Tooltip>
  );
};
